import '../styles/PageGlobal.css';
import './GestionStockPage.css';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom'; 
import ReferenceButton from '../components/ReferenceButton';
import { ExportService } from '../services/ExportService.js';
import { GestionStockService } from '../services/GestionStockService.js';

const TABS = [
  { key: 'reception',   label: 'Réception',      color: '#dc3545' },
  { key: 'attribution', label: 'Attribution',    color: '#28a745' },
  { key: 'stock',       label: 'État du Stock'   /* pas de couleur = style par défaut */ },
];

export default function GestionStockPage() {
  const navigate = useNavigate(); 
  const exportService = useMemo(() => new ExportService(), []);
  const gestionStockService = useMemo(() => new GestionStockService(), []);

  const [activeTab, setActiveTab] = useState('reception');
  const [searchQuery, setSearchQuery] = useState('');

  // ÉTATS
  const [attributionData, setAttributionData] = useState([]);
  const [attributionLoading, setAttributionLoading] = useState(true);
  const [attributionError, setAttributionError] = useState(null);

  const [receptionData, setReceptionData] = useState([]);
  const [receptionLoading, setReceptionLoading] = useState(true);
  const [receptionError, setReceptionError] = useState(null);

  const [stockData, setStockData] = useState([]);
  const [stockLoading, setStockLoading] = useState(true);
  const [stockError, setStockError] = useState(null);

  // ÉDITION
  const [editingAttributionId, setEditingAttributionId] = useState(null);
  const [attributionEdits, setAttributionEdits] = useState({});

  const [editingReceptionId, setEditingReceptionId] = useState(null);
  const [receptionEdits, setReceptionEdits] = useState({});

  // ── Data loader ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (tab) => {
    try {

              if (tab === 'attribution') {
              setAttributionLoading(true);
              setAttributionError(null);

              try {
                const [mandatsData, stockData] = await Promise.all([
                  gestionStockService.fetchOpenMandats(),
                  gestionStockService.fetchAvailableStock() // Cet endpoint existe déjà et fonctionne
                ]);

                const stockMap = {};
                stockData.forEach(stock => {
                  const ref = stock.product?.refFabricant;
                  if (ref) {
                    stockMap[ref] = stock.quantity || 0;
                  }
                });

                // Dans le bloc if (tab === 'attribution') de fetchData :
                const formattedData = (mandatsData || []).map(mandat => ({
                  id: mandat.id,
                  bonCommande: mandat.reference || `Mandat-${mandat.id}`,
                  client: mandat.clientNom || 'Client N/A',
                  lignes: (mandat.lines || []).map(line => {
                    const refFab = line.refFabricant || 'N/A';
                    return {
                      id: line.id,
                      productId: line.productId,
                      ref: line.reference || 'N/A',
                      refFabricant: refFab,
                      mandatDemande: line.quantity || 0,                 // Demande totale (ex: 10)
                      dispoStock: stockMap[refFab] !== undefined ? stockMap[refFab] : 0,
                      quantityPartAttributed: line.quantityPartAttributed || 0, //  Somme des attributions (ex: 2, 5, 10)
                      status: line.status || 'OPEN',                     //  Statut : 'OPEN', 'PARTIAL', 'ATTRIBUTED'
                      commentaire: ''
                    };
                  })
                }));
                
                setAttributionData(formattedData);
              } catch (err) {
                setAttributionError(err.message);
              } finally {
                setAttributionLoading(false);
              }
            }
      else if (tab === 'reception') {
        setReceptionLoading(true); setReceptionError(null);
        const data = await gestionStockService.fetchAllBCF();
        const formattedData = (data || []).map(bcf => ({
          id: bcf.id,
          bonCommande: bcf.reference || `BCF-${bcf.id}`,
          client: bcf.fournisseurNom || 'Fournisseur',
          lignes: (bcf.lines || bcf.lignes || []).map(line => {
            const orderedQty = line.quantity || 0;
            const alreadyReceived = line.receivedQuantity || 0;
            return {
              id: line.id,
              ref: line.reference || line.refFabricant || 'N/A',
              ean: line.ean || 'N/A',
              mandatDemande: orderedQty,
              alreadyReceived: alreadyReceived,
              remainingToReceive: Math.max(0, orderedQty - alreadyReceived),
              dispoStock: 0,
              quantity: 0,
              fournisseur: bcf.fournisseurNom || 'N/A',
              statutReception: line.status || 'DRAFT',
              commentaire: ''
            };
          })
        }));
        setReceptionData(formattedData);
      } 
      else if (tab === 'stock') {
        setStockLoading(true); setStockError(null);
        try {
          const data = await gestionStockService.fetchAvailableStock();
          const formattedData = (data || []).map(stock => {
            const product = stock.product || {}; 
            return {
              id: stock.id,
              ref: product.refInterne || product.reference || stock.ref || 'N/A',
              ean: product.ean || stock.ean || 'N/A',
              currentQuantity: stock.quantity || 0,
              lastMovementType: stock.lastMovementType || 'RECEPTION',
              lastMovementDate: stock.lastMovementDate || null
            };
          });
          setStockData(formattedData);
        } catch (err) {
          setStockError(err.message);
        } finally {
          setStockLoading(false);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      if (tab === 'attribution') setAttributionError(err.message);
      if (tab === 'reception') setReceptionError(err.message);
      if (tab === 'stock') setStockError(err.message);
    } finally {
      if (tab === 'attribution') setAttributionLoading(false);
      if (tab === 'reception') setReceptionLoading(false);
      if (tab === 'stock') setStockLoading(false);
    }
  }, [gestionStockService]);

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab, fetchData]);

  // ── Handlers d'Édition et d'Action (Attribution) ────────────────────────────
  const toggleAttributionEditing = (commande) => {
    if (editingAttributionId === commande.id) {
      setEditingAttributionId(null);
      setAttributionEdits({});
      return;
    }
    const initialEdits = {};
    (commande.lignes || []).forEach((ligne) => {
      initialEdits[ligne.id] = ligne.quantity ?? 0;
    });
    setAttributionEdits(initialEdits);
    setEditingAttributionId(commande.id);
  };

  const handleAttributionInputChange = (ligneId, value) => {
    setAttributionEdits((prev) => ({ ...prev, [ligneId]: value }));
  };

  // 🟢 SAVE : Envoie la valeur finale préférée après les ajustements "football"
  const handleSaveAttribution = async (commande) => {
    try {
      let successCount = 0;
      
      // On parcourt les lignes pour envoyer uniquement celles qui ont une quantité > 0
      for (const ligne of commande.lignes) {
        const qtyToAttribute = Number(attributionEdits[ligne.id] ?? 0);
        
        if (qtyToAttribute > 0) {
          const payload = {
            productId: ligne.productId, 
            mandatId: commande.id,
            quantityToAttribute: qtyToAttribute
          };
          await gestionStockService.postAttribution(payload);
          successCount++;
        }
      }

      setEditingAttributionId(null);
      setAttributionEdits({});
      alert(successCount > 0 ? `${successCount} attribution(s) effectuée(s) avec succès !` : "Aucune quantité à attribuer.");
      
      // 🟢 Recharger pour voir le stock diminuer et les lignes mises à jour
      fetchData('attribution');
      
    } catch (err) {
      setAttributionError(err.message);
      alert("Erreur lors de l'attribution : " + err.message);
    }
  };

  const handleAttributeAll = (commande) => {
  const pool = {};   // stock restant par réf. fabricant (évite de compter 2 fois le même stock)
  const edits = {};

  (commande.lignes || []).forEach((ligne) => {
    const already = ligne.quantityPartAttributed || 0;
    const demanded = ligne.mandatDemande || 0;
    const key = ligne.refFabricant;

    if (pool[key] === undefined) pool[key] = ligne.dispoStock || 0;

    const needed = Math.max(0, demanded - already);
    const extra = Math.min(needed, pool[key]);
    pool[key] -= extra;

    edits[ligne.id] = already + extra; // valeur finale (déjà attribué + nouveau)
  });

  setAttributionEdits(edits);
  setEditingAttributionId(commande.id); // affiche les inputs
  };

  // 🟢 RELEASE : Annule l'attribution et rend le stock au pool global
  const handleReleaseAttribution = async (mandatLineId) => {
    if (!window.confirm("Confirmer la libération de ce stock vers le stock global ?\nCette action remettra l'attribution de cette ligne à 0.")) {
      return;
    }
    
    try {
      // 1. Appel au backend
      await gestionStockService.deleteReleaseFromMandat(mandatLineId);
      
      // 2. Message de succès
      alert("Stock libéré et retourné au pool global avec succès !");
      
      // 3. 🟢 Rafraîchissement immédiat de l'onglet pour voir le stock remonter et la colonne "Déjà Attribué" passer à 0
      fetchData('attribution');
      
    } catch (err) {
      alert("Erreur lors de la libération : " + err.message);
    }
  };

  // ── Handlers d'Édition et d'Action (Réception) ──────────────────────────────
  const toggleReceptionEditing = (commande) => {
    if (editingReceptionId === commande.id) { setEditingReceptionId(null); return; }
    const initialEdits = {};
    (commande.lignes || []).forEach((ligne) => { initialEdits[ligne.id] = ligne.quantity ?? 0; });
    setReceptionEdits(initialEdits);
    setEditingReceptionId(commande.id);
  };

  const handleReceptionInputChange = (ligneId, value) => setReceptionEdits((prev) => ({ ...prev, [ligneId]: value }));
  
  const handleReceiveAll = (commande) => {
  const edits = {};
  (commande.lignes || []).forEach((ligne) => {
    edits[ligne.id] = ligne.remainingToReceive; // = Commandé - Déjà reçu
  });
  setReceptionEdits(edits);
  setEditingReceptionId(commande.id); // passe en mode édition pour afficher les inputs
  };

  const handleSaveReception = async (commande) => {
    try {
      const updatedLignes = commande.lignes.map((ligne) => ({ ...ligne, quantity: Number(receptionEdits[ligne.id] ?? 0) }));
      await gestionStockService.saveReception(updatedLignes);
      setReceptionData((prevData) => prevData.map((c) => c.id !== commande.id ? c : { ...c, lignes: updatedLignes }));
      setEditingReceptionId(null);
      alert("Réception(s) effectuée(s) avec succès !");
      fetchData('reception');
    } catch (err) { setReceptionError(err.message); alert("Erreur : " + err.message); }
  };

  const handleSave = async (id) => fetchData(activeTab);
  const handleRecherche = (e) => setSearchQuery(e.target.value);

  const handleAjoutProduits = () => {
    let userId = null;
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          userId = payload.userId;
        }
      }
    } catch (error) {
      console.error("Erreur token :", error);
    }

    if (userId) {
      navigate(`/achat/${userId}`, { state: { openTab: 'approvisionnement', openModal: true } });
    } else {
      navigate('/achat'); 
    }
  };
  
  const handleExportInventaire = async () => {
    try {
      const blob = await exportService.exportInventory();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'inventaire_export.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { alert("Erreur d'export : " + err.message); }
  };


  // ── Fonctions de rendu ──────────────────────────────────────────────────────
    const renderAttributionTable = (data) => {
    const safeData = Array.isArray(data) ? data : [];
    
    return (
      <table className="stock-table">
        <thead>
          <tr>
            <th>N°</th>
            <th>Mandat / Client</th>
            <th>Ref. Désignation</th>
            <th>Ref. Fabricant</th>
            <th>Demandé</th>
            <th>Dispo Stock</th>
            <th>Déjà Attribué</th> {/* 🟢 NOUVELLE COLONNE */}
            <th>À Attribuer</th>
            <th>Statut</th>
            <th>Action</th>
          </tr>
        </thead>
        
        <tbody>
          {safeData.length === 0 ? (
            <tr>
              {/* 🟢 colSpan mis à jour à 10 colonnes */}
              <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                Aucun mandat ouvert à traiter.
              </td>
            </tr>
          ) : (
            safeData.map((commande) => {
              const lignes = Array.isArray(commande.lignes) ? commande.lignes : [];
              const isEditing = editingAttributionId === commande.id;

              const canAttribute = lignes.some(
                (l) => (l.mandatDemande || 0) > (l.quantityPartAttributed || 0) && (l.dispoStock || 0) > 0
              );

              return lignes.map((ligne, ligneIndex) => {
                const alreadyAttributed = ligne.quantityPartAttributed || 0;
                const demanded = ligne.mandatDemande || 0;
                
                // Le max possible permet de descendre (libérer) ou monter (attribuer) sans dépasser la demande ou le stock réel
                const maxPossible = Math.min(demanded, alreadyAttributed + ligne.dispoStock);
                const editQty = Number(attributionEdits[ligne.id] ?? alreadyAttributed);

                return (
                  <tr
                    key={`${commande.id}-${ligne.id}`}
                    className={ligneIndex === 0 ? 'stock-group-start' : 'stock-group-line'}
                  >
                    {ligneIndex === 0 && <td rowSpan={lignes.length} className="stock-td-center">{commande.id}</td>}
                    {ligneIndex === 0 && (
                      <td rowSpan={lignes.length} className="stock-client-cell">
                        <div className="stock-bon">{commande.bonCommande}</div>
                        <div className="stock-client">{commande.client}</div>
                        <div className="stock-row-actions">
                          <button className="stock-btn stock-btn-blue" onClick={() => toggleAttributionEditing(commande)}>
                            {isEditing ? 'Annuler' : 'Ajuster'}
                          </button>
                          <button
                            className="stock-btn stock-btn-green"
                            onClick={() => handleAttributeAll(commande)}
                            disabled={!canAttribute}
                            title={canAttribute ? 'Remplir avec le maximum possible depuis le stock' : 'Rien à attribuer (déjà complet ou stock à 0)'}
                          >
                            Tout attribuer
                          </button>
                          {isEditing && (
                            <button className="stock-btn stock-btn-yellow" onClick={() => handleSaveAttribution(commande)}>
                              Save
                            </button>
                          )}
                        </div>
                      </td>
                    )}

                    <td className="stock-ref">{ligne.ref}</td>
                    <td>{ligne.refFabricant}</td>
                    <td>{demanded}</td>
                    
                    <td style={{ fontWeight: 'bold', color: ligne.dispoStock > 0 ? 'green' : 'red' }}>
                      {ligne.dispoStock}
                    </td>

                    {/* 🟢 COLONNE : DÉJÀ ATTRIBUÉ (Lecture seule, claire et visible) */}
                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: alreadyAttributed > 0 ? '#0056b3' : '#999' }}>
                      {alreadyAttributed}
                    </td>

                    {/* 🟢 COLONNE : À ATTRIBUER (Input modifiable) */}
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          className="stock-edit-input"
                          value={editQty}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            // On accepte la valeur seulement si elle est entre 0 et maxPossible
                            if (val >= 0 && val <= maxPossible) {
                              handleAttributionInputChange(ligne.id, val);
                            }
                          }}
                          min="0"
                          max={maxPossible}
                          style={{ width: '80px', textAlign: 'center', fontWeight: 'bold', border: '2px solid #007bff' }}
                        />
                      ) : (
                        <span style={{ color: alreadyAttributed > 0 ? 'green' : '#555', fontWeight: 'bold' }}>
                          {alreadyAttributed === demanded && demanded > 0 ? '✅' : '-'}
                        </span>
                      )}
                    </td>

                    {/*  COLONNE : STATUT VISUEL */}
                    <td>
                      {ligne.status === 'ATTRIBUTED' && <span style={{color: 'green', fontWeight: 'bold'}}> Attribué</span>}
                      {ligne.status === 'PARTIAL' && <span style={{color: 'orange', fontWeight: 'bold'}}> Partiel</span>}
                      {(ligne.status === 'OPEN' || !ligne.status) && <span style={{color: '#555'}}> Ouvert</span>}
                    </td>

                    <td className="stock-action-cell">
                      {/* Le bouton n'apparaît que si quelque chose a été attribué et qu'on n'est pas en mode édition */}
                      {alreadyAttributed > 0 && !isEditing && (
                        <button 
                          className="stock-btn stock-btn-red" 
                          onClick={() => handleReleaseAttribution(ligne.id)}
                          title="Remettre l'attribution à 0 et rendre le stock"
                        >
                          Désattribuer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              });
            })
          )}
        </tbody>
      </table>
    );
  };

  const renderReceptionTable = (data) => {
    const safeData = Array.isArray(data) ? data : [];

    
    return (
      <table className="stock-table">
        <thead>
          <tr>
            <th>N</th><th>BCF/Approvisionnement</th><th>Ref. Désignation</th><th>EAN</th>
            <th>Commandé</th><th>Reçu / Reste</th><th>À réceptionner</th>
            <th>Fournisseur</th><th>Statut</th><th>Commentaire</th>
          </tr>
        </thead>
        <tbody>
          {safeData.map((commande) => {
            const lignes = Array.isArray(commande.lignes) ? commande.lignes : [];
            const isEditing = editingReceptionId === commande.id;
       
            const hasRemaining = lignes.some((l) => l.remainingToReceive > 0);


            return lignes.map((ligne, ligneIndex) => (
              <tr
                key={`${commande.id}-${ligne.id || ligneIndex}`}
                className={ligneIndex === 0 ? 'stock-group-start' : 'stock-group-line'}
              >
                {ligneIndex === 0 && <td rowSpan={lignes.length || 1} className="stock-td-center">{commande.id}</td>}
                {ligneIndex === 0 && (
                  <td rowSpan={lignes.length || 1} className="stock-client-cell">
                    <div className="stock-bon">{commande.bonCommande}</div>
                    <div className="stock-client">{commande.client}</div>
                    <div className="stock-row-actions">
                      <button className="stock-btn stock-btn-blue" onClick={() => toggleReceptionEditing(commande)}>
                        {isEditing ? 'Annuler' : 'Réception'}
                      </button>

                      <button
                        className="stock-btn stock-btn-green"
                        onClick={() => handleReceiveAll(commande)}
                        disabled={!hasRemaining}
                        title={hasRemaining ? 'Remplir toutes les lignes avec le reste à recevoir' : 'Tout est déjà reçu'}
                      >
                        Tout reçu
                      </button>
                      
                      <button className="stock-btn stock-btn-yellow" onClick={() => (isEditing ? handleSaveReception(commande) : handleSave(commande.id))}>
                        Save
                      </button>
                    </div>
                  </td>
                )}
                <td className="stock-ref">{ligne.ref}</td>
                <td>{ligne.ean}</td>
                <td>{ligne.mandatDemande}</td>
                <td>
                  {ligne.alreadyReceived} / {ligne.mandatDemande}
                  {ligne.remainingToReceive > 0 && (
                    <div style={{ color: 'orange', fontSize: '0.85em' }}>(reste {ligne.remainingToReceive})</div>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input type="number" className="stock-edit-input" value={receptionEdits[ligne.id] ?? 0} onChange={(e) => handleReceptionInputChange(ligne.id, e.target.value)} min="0" max={ligne.remainingToReceive} />
                  ) : (
                    ligne.quantity > 0 ? <span style={{ color: 'green', fontWeight: 'bold' }}>+{ligne.quantity}</span> : '-'
                  )}
                </td>
                <td>{ligne.fournisseur}</td>
                <td>{ligne.statutReception}</td>
                <td>{ligne.commentaire}</td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    );
  };

  const renderStockTable = (data) => {
    const safeData = Array.isArray(data) ? data : [];
    return (
      <table className="stock-table">
        <thead>
          <tr>
            <th>N</th><th>Ref. Désignation</th><th>EAN</th><th>Quantité en Stock</th>
            <th>Dernier Mouvement</th><th>Date du mouvement</th><th>Action</th>
          </tr>
        </thead>
        <tbody>
          {safeData.length === 0 ? (
            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>Aucun produit en stock.</td></tr>
          ) : (
            safeData.map((item, index) => (
              <tr key={item.id || index}>
                <td className="stock-td-center">{index + 1}</td>
                <td className="stock-ref">{item.ref || 'N/A'}</td>
                <td>{item.ean || 'N/A'}</td>
                <td style={{ fontWeight: 'bold', color: (item.currentQuantity || 0) > 0 ? 'green' : 'red' }}>
                  {item.currentQuantity || 0}
                </td>
                <td>
                  {item.lastMovementType === 'RECEPTION' && <span style={{color: 'green'}}>📥 Réception</span>}
                  {item.lastMovementType === 'ATTRIBUTION' && <span style={{color: 'orange'}}>📤 Attribution</span>}
                  {!item.lastMovementType && 'N/A'}
                </td>
                <td>{item.lastMovementDate ? new Date(item.lastMovementDate).toLocaleString('fr-FR') : 'N/A'}</td>
                <td className="stock-action-cell">
                  <button className="stock-btn stock-btn-blue">Historique</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };

  // ── Render Principal ────────────────────────────────────────────────────────
  return (
    <div className="containerMain stock-container">
      <div className="stock-header-row" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
        <h1 className="stock-title">
          {activeTab === 'attribution' && "Attribution des produits"}
          {activeTab === 'reception'   && "Réception des produits"}
          {activeTab === 'stock'       && "État actuel du Stock"}
        </h1>
        {activeTab === 'stock' && (
          <ReferenceButton onClick={handleExportInventaire} style={{ backgroundColor: '#28a745', color: 'white', fontWeight: 'bold' }}>
            📥 Exporter l'inventaire
          </ReferenceButton>
        )}
      </div>

      <input type="text" className="stock-search-input" placeholder="Recherche par référence..." value={searchQuery} onChange={handleRecherche} />

     <div className="stock-top-buttons">
  {TABS.map((tab) => (
    <ReferenceButton
      key={tab.key}
      onClick={() => setActiveTab(tab.key)}
      className="stock-tab-btn"
      style={tab.color ? { backgroundColor: tab.color, color: 'white' } : undefined}
    >
      {tab.label}
    </ReferenceButton>
  ))}
</div>

      <div className="stock-table-wrapper">
        {activeTab === 'attribution' && attributionLoading && <div>Chargement…</div>}
        {activeTab === 'attribution' && attributionError && <div className="stock-error">Erreur: {attributionError}</div>}
        {!attributionLoading && !attributionError && activeTab === 'attribution' && renderAttributionTable(attributionData)}

        {activeTab === 'reception' && receptionLoading && <div>Chargement…</div>}
        {activeTab === 'reception' && receptionError && <div className="stock-error">Erreur: {receptionError}</div>}
        {!receptionLoading && !receptionError && activeTab === 'reception' && renderReceptionTable(receptionData)}

        {activeTab === 'stock' && stockLoading && <div>Chargement du stock…</div>}
        {activeTab === 'stock' && stockError && <div className="stock-error">Erreur stock: {stockError}</div>}
        {!stockLoading && !stockError && activeTab === 'stock' && renderStockTable(stockData)}
      </div>

      <div className="stock-footer">
        <div className="stock-footnotes">
          <p>*Le stock affiché lors de l'édition est calculé en temps réel (Stock initial - Quantité saisie).</p>
          <p>*Utilisez les boutons "Attribuer" et "Libérer" pour ajuster les quantités avant de cliquer sur "Save".</p>
          <p>*L'ajout produits personnalisé se fait via le module d'Approvisionnement.</p>
        </div>
        <ReferenceButton onClick={handleAjoutProduits}>➕ Ajout produits / Approvisionnement</ReferenceButton>
      </div>
    </div>
  );
}